import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { hashStr } from "@/lib/hash";
import {
  MagnifyingGlass, LinkSimple, Newspaper, ArrowSquareOut, Clock, Globe, MapPin, Sparkle,
} from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";
import ListenTranslateBar from "@/components/ListenTranslateBar";

/**
 * News — the daily nature-news archive. A slimmer, publisher-first surface
 * over the same `nature_stories` collection, filtered to `story_type=news_link`
 * so readers see a curated stream of weather, wildlife, food-security and
 * climate coverage from NASA, NOAA, WMO, FAO, IUCN, Mongabay, AccuWeather,
 * FDA, USDA, EPA, WHO and Google News-topic queries.
 */

const CATEGORY_CHIPS = [
  { slug: "",                    label: "All news",       emoji: "🗞" },
  { slug: "climate",             label: "Climate",        emoji: "🌡" },
  { slug: "weather",             label: "Weather & Sky",  emoji: "🌦" },
  { slug: "plants",              label: "Plants",         emoji: "🌱" },
  { slug: "trees",               label: "Trees & Forests",emoji: "🌳" },
  { slug: "agriculture",         label: "Agriculture",    emoji: "🌾" },
  { slug: "fruits",              label: "Fruits",         emoji: "🍎" },
  { slug: "vegetables",          label: "Vegetables",     emoji: "🥕" },
  { slug: "birds",               label: "Birds",          emoji: "🐦" },
  { slug: "wildlife",            label: "Wildlife",       emoji: "🦌" },
  { slug: "mammals",             label: "Mammals",        emoji: "🐻" },
  { slug: "insects",             label: "Pollinators",    emoji: "🐝" },
  { slug: "fisheries",           label: "Aquatic Life",   emoji: "🐟" },
  { slug: "oceans",              label: "Oceans & Shores",emoji: "🌊" },
  { slug: "freshwater",          label: "Water",          emoji: "💧" },
  { slug: "floods",              label: "Floods",         emoji: "🌊" },
  { slug: "drought",             label: "Drought",        emoji: "🏜" },
  { slug: "wildfires",           label: "Wildfires",      emoji: "🔥" },
  { slug: "geological",          label: "Land & Geology", emoji: "🪨" },
  { slug: "landslide",           label: "Landslides",     emoji: "⛰" },
  { slug: "biodiversity",        label: "Biodiversity",   emoji: "🌺" },
  { slug: "invasive",            label: "Invasive",       emoji: "🌿" },
  { slug: "conservation",        label: "Conservation",   emoji: "💚" },
  { slug: "pollution",           label: "Pollution",      emoji: "🏭" },
  { slug: "air-quality",         label: "Air quality",    emoji: "🌫" },
  { slug: "soil",                label: "Soil",           emoji: "🪱" },
  { slug: "food-security",       label: "Food security",  emoji: "🍚" },
  { slug: "livestock",           label: "Livestock",      emoji: "🐄" },
  { slug: "energy",              label: "Energy & Nature",emoji: "⚡" },
  { slug: "health",              label: "Nature & Health",emoji: "🌿" },
  { slug: "records",             label: "Records",        emoji: "📊" },
  { slug: "unusual",             label: "Unusual events", emoji: "✨" },
  { slug: "other-natural-event", label: "Other events",   emoji: "🌍" },
  { slug: "good-news",           label: "Good news",      emoji: "🌈" },
  { slug: "science",             label: "Science",        emoji: "🔬" },
  { slug: "policy",              label: "Policy",         emoji: "🏛" },
  { slug: "indigenous",          label: "Indigenous",     emoji: "🪶" },
];

function NewsCard({ n, categoryCovers, categoryCoverVariants }) {
  const storyCover = n.media?.find((m) => m.media_type === "image")?.url;
  // Random category cover pick — the /category-covers endpoint returns up
  // to 4 variants per slug (see backend/scripts/generate_category_covers.py).
  // Choose deterministically by story id so the same card doesn't flicker on
  // every render / route change.
  const variantCover = (() => {
    const vs = categoryCoverVariants?.[n.category];
    if (!vs?.length) return null;
    const idx = Math.abs(hashStr(n.id || n.canonical_url || "")) % vs.length;
    return vs[idx];
  })();
  const cover = storyCover || variantCover || categoryCovers?.[n.category] || null;
  const loc = [n.location?.city, n.location?.state, n.location?.country].filter(Boolean).join(", ");
  const date = n.event_date || n.publication_date || n.created_at;
  // External news_link items skip the interim NaturePulse detail page and
  // send the reader directly to the publisher in a new tab. Internal
  // stories, articles and observations stay inside the app.
  const isExternal = n.story_type === "news_link" && !!(n.canonical_url || n.url);
  const externalUrl = n.canonical_url || n.url;
  const Wrapper = ({ children, className }) => (
    isExternal
      ? <a href={externalUrl} target="_blank" rel="noreferrer noopener" className={className} data-testid={`news-card-external-${n.id}`}>{children}</a>
      : <Link to={`/stories/${n.id}`} className={className}>{children}</Link>
  );
  return (
    <div className="rounded-2xl overflow-hidden bg-white/70 border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] transition-colors flex flex-col" data-testid={`news-card-${n.id}`}>
      <Wrapper className="group block flex-1">
        {cover ? (
          <div className="aspect-[16/9] overflow-hidden bg-black/5 relative">
            <img src={cover} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
            {!storyCover && (
              <span
                className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-white"
                data-testid={`news-card-illustrated-${n.id}`}
                title="AI-illustrated category cover"
              >
                <Sparkle size={9} weight="fill" /> Illustrated
              </span>
            )}
            {isExternal && (
              <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-white/85 backdrop-blur px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#241209]">
                <ArrowSquareOut size={9} weight="bold" /> External
              </span>
            )}
          </div>
        ) : (
          <div className="aspect-[16/6] bg-gradient-to-br from-[#FBF2E1] to-[#F5EBDC] border-b border-black/5 grid place-items-center">
            <Newspaper size={26} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]/70" />
          </div>
        )}
        <div className="p-4 md:p-5">
          <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
            <Newspaper size={11} weight="fill" />
            <span>News</span>
            {n.category && <span className="text-[color:var(--np-muted,#3A3830)]">· {n.category}</span>}
          </div>
          <h3 className="mt-2 font-serif-display text-lg leading-[1.2] tracking-tight line-clamp-3">{n.title || n.headline}</h3>
          {n.summary && <p className="mt-2 text-sm text-[#3A3830] line-clamp-3">{n.summary}</p>}
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[color:var(--np-muted,#3A3830)] font-mono-tech tracking-wide">
            {n.publisher && <span className="text-[#1B4332]">— {n.publisher}</span>}
            {loc && <span className="inline-flex items-center gap-1"><MapPin size={10} weight="fill" /> {loc}</span>}
            {date && <span className="inline-flex items-center gap-1"><Clock size={10} weight="fill" /> {new Date(date).toISOString().slice(0, 10)}</span>}
          </div>
        </div>
      </Wrapper>
      <div className="px-4 md:px-5 pb-4 flex items-center gap-2">
        {n.canonical_url && (
          <a href={n.canonical_url} target="_blank" rel="noreferrer" data-testid={`news-source-${n.id}`}
             className="inline-flex items-center gap-1 rounded-full bg-[#241209] text-[#F5EBDC] px-3 py-1.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase hover:opacity-90">
            <ArrowSquareOut size={11} weight="bold" /> Read the original
          </a>
        )}
        {n.publisher && (
          <span className="text-[10px] text-[#3A3830] font-mono-tech tracking-[0.16em] uppercase truncate max-w-[140px]" title={n.publisher}>
            {n.publisher}
          </span>
        )}
        <div className="ml-auto">
          <ShareButton
            url={`${window.location.origin}/stories/${n.id}`}
            title={n.title || n.headline}
            text={n.summary || ""}
            shortKind="story" shortId={n.id}
            variant="icon" testid={`news-share-${n.id}`}
          />
        </div>
      </div>
    </div>
  );
}

export default function News() {
  const [params, setParams] = useSearchParams();
  const [news, setNews] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [categoryCovers, setCategoryCovers] = useState({});
  const [categoryCoverVariants, setCategoryCoverVariants] = useState({});
  const category = params.get("category") || "";
  const country = params.get("country") || "";
  const PAGE = 24;

  useEffect(() => {
    http.get("/nature/stories/category-covers")
      .then(({ data }) => {
        setCategoryCovers(data?.covers || {});
        setCategoryCoverVariants(data?.variants || {});
      })
      .catch(() => { setCategoryCovers({}); setCategoryCoverVariants({}); });
  }, []);

  const load = useCallback(async (nextOffset = 0, append = false) => {
    setLoading(true);
    try {
      const { data } = await http.get("/nature/stories/list/all", {
        params: {
          story_type: "news_link",
          category: category || undefined,
          country: country || undefined,
          q: q || undefined,
          limit: PAGE, offset: nextOffset,
        },
      });
      setNews((prev) => append ? [...prev, ...(data.stories || [])] : (data.stories || []));
      setTotal(data.total || 0);
      setOffset(nextOffset);
    } finally { setLoading(false); }
  }, [category, country, q]);

  useEffect(() => { load(0, false); }, [load]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(params);
    if (slug) p.set("category", slug); else p.delete("category");
    setParams(p, { replace: true });
  };

  return (
    <main className="bg-[#F5EBDC] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32" data-testid="news-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="NaturePulse News — the latest stories, discoveries and research from the living world." testidPrefix="ltbar-news" />
      </div>
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
              <Newspaper size={12} weight="fill" /> / News · Today on Planet Earth
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              Weather, nature & food news — from every credible source.
            </h1>
            <p className="mt-2 font-serif-display text-base md:text-lg italic text-[#3A3830] max-w-3xl">
              A living archive of environmental journalism from NASA, NOAA, WMO, FAO, IUCN, EPA, FDA, USDA, WHO, Mongabay, AccuWeather and topic-filtered Google News queries — one canonical record per event, links back to every original publisher.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Link
              to="/stories/new"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
            >
              + Share a link
            </Link>
            <ShareButton
              title="NaturePulse News — Today on Planet Earth"
              text="A living archive of weather, nature and food news from credible sources."
              hashtags={["NaturePulse", "ClimateAction"]}
              variant="chip" testid="news-share"
            />
          </div>
        </div>

        {/* Search */}
        <div className="mt-6 flex items-center gap-2 rounded-full border border-black/15 bg-white/70 backdrop-blur px-4 py-2">
          <MagnifyingGlass size={14} className="text-[color:var(--np-muted,#3A3830)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search headlines, publishers, tags…"
            data-testid="news-search"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-[color:var(--np-muted,#3A3830)]"
          />
          <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
            {total} record{total === 1 ? "" : "s"}
          </span>
        </div>

        {/* Category chips */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="news-category-chips">
          {CATEGORY_CHIPS.map((c) => (
            <button
              key={c.slug || "all"}
              onClick={() => setCategory(c.slug)}
              data-testid={`news-cat-${c.slug || "all"}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border font-mono-tech text-[10px] tracking-[0.22em] uppercase ${
                category === c.slug
                  ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]"
                  : "border-black/15 text-[#1B4332] bg-white/60 hover:bg-white"
              }`}
            >
              <span aria-hidden>{c.emoji}</span> {c.label}
            </button>
          ))}
        </div>

        {/* AI-illustrated category boards — top visual panel */}
        {Object.keys(categoryCovers).length > 0 && (
          <div className="mt-6" data-testid="news-category-boards">
            <div className="flex items-baseline justify-between mb-2">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
                <Sparkle size={11} weight="fill" /> Category boards · AI illustrations
              </div>
              {category && (
                <button
                  onClick={() => setCategory("")}
                  data-testid="news-boards-clear"
                  className="font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline"
                >
                  Clear filter
                </button>
              )}
            </div>
            <div className="-mx-1 overflow-x-auto pb-2">
              <div className="flex gap-3 px-1 min-w-max">
                {CATEGORY_CHIPS.filter((c) => c.slug && categoryCovers[c.slug]).map((c) => {
                  // Random variant picker — deterministic on slug so the
                  // strip is stable within one session but different from
                  // the card grid below (different variant per surface).
                  const vs = categoryCoverVariants[c.slug];
                  const url = (vs?.length
                    ? vs[Math.abs(hashStr("board-" + c.slug)) % vs.length]
                    : null) || categoryCovers[c.slug];
                  const active = category === c.slug;
                  return (
                    <button
                      key={c.slug}
                      onClick={() => setCategory(active ? "" : c.slug)}
                      data-testid={`news-board-${c.slug}`}
                      aria-selected={active}
                      className={`relative shrink-0 w-40 md:w-48 aspect-[4/5] rounded-2xl overflow-hidden border transition-all group ${
                        active
                          ? "border-[color:var(--np-brand,#B85F2A)] ring-2 ring-[color:var(--np-brand,#B85F2A)]/40 scale-[1.02]"
                          : "border-black/10 hover:border-[color:var(--np-brand,#B85F2A)]/60 hover:-translate-y-0.5"
                      }`}
                    >
                      <img src={url} alt={c.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-3 text-left">
                        <div className="text-xl leading-none">{c.emoji}</div>
                        <div className="mt-1 font-serif-display text-white text-base leading-tight drop-shadow line-clamp-2">
                          {c.label}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] flex items-center gap-2">
          <Globe size={11} weight="fill" /> Auto-refreshed nightly from ~50 curated feeds. New items auto-illustrated by AI.
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 mt-8">
        {loading && news.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-64 rounded-2xl bg-white/50 border border-black/10 animate-pulse" />
            ))}
          </div>
        ) : news.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-black/15 bg-white/40 p-10 text-center">
            <div className="font-serif-display text-2xl">No news matches this filter yet.</div>
            <p className="mt-2 text-sm text-[#3A3830]">
              <button onClick={() => setCategory("")} className="underline text-[color:var(--np-brand,#B85F2A)]">Clear category</button>
              {" "}or <Link to="/stories/new" className="underline text-[color:var(--np-brand,#B85F2A)]">share a link</Link>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="news-grid">
            {news.map((n) => <NewsCard key={n.id} n={n} categoryCovers={categoryCovers} categoryCoverVariants={categoryCoverVariants} />)}
          </div>
        )}

        {!loading && news.length > 0 && news.length < total && (
          <div className="mt-8 text-center">
            <button
              onClick={() => load(offset + PAGE, true)}
              data-testid="news-load-more"
              className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white text-[#1B4332] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-black/5"
            >
              Load more — {total - news.length} left
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
